/**
 * User Preferences Context for ParlSearch
 * Manages user preferences state and sync with backend
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PreferencesContext = createContext(null);

// Local storage key for preferences cache
const PREFERENCES_KEY = 'parlsearch_preferences';

// Default preferences (matches backend defaults)
const DEFAULT_PREFERENCES = {
  results_per_page: 25,
  default_sort: 'relevance',
  snippet_length: 'medium',
  theme: 'system',
  default_search_types: [],
  email_notifications: true,
  email_frequency: 'daily',
};

export function PreferencesProvider({ children }) {
  const { isAuthenticated, getToken, user } = useAuth();
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch preferences from backend
  const fetchPreferences = useCallback(async () => {
    if (!isAuthenticated) {
      // Load from localStorage for non-authenticated users
      const stored = localStorage.getItem(PREFERENCES_KEY);
      if (stored) {
        try {
          setPreferences({ ...DEFAULT_PREFERENCES, ...JSON.parse(stored) });
        } catch (e) {
          // Invalid stored preferences
        }
      }
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences(response.data);
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(response.data));
    } catch (err) {
      console.error('Failed to fetch preferences:', err);
      setError('Failed to load preferences');
      // Fall back to cached preferences
      const stored = localStorage.getItem(PREFERENCES_KEY);
      if (stored) {
        try {
          setPreferences({ ...DEFAULT_PREFERENCES, ...JSON.parse(stored) });
        } catch (e) {
          // Use defaults
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getToken]);

  // Fetch preferences when auth state changes
  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences, user]);

  // Update preferences
  const updatePreferences = useCallback(async (updates) => {
    if (!isAuthenticated) {
      // Store locally for non-authenticated users
      const newPrefs = { ...preferences, ...updates };
      setPreferences(newPrefs);
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(newPrefs));
      return { success: true };
    }

    setLoading(true);
    setError(null);

    try {
      const token = getToken();
      const response = await axios.put(
        `${API_URL}/api/preferences`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreferences(response.data);
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(response.data));
      return { success: true };
    } catch (err) {
      console.error('Failed to update preferences:', err);
      const message = err.response?.data?.detail || 'Failed to save preferences';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getToken, preferences]);

  // Reset preferences to defaults
  const resetPreferences = useCallback(async () => {
    if (!isAuthenticated) {
      setPreferences(DEFAULT_PREFERENCES);
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(DEFAULT_PREFERENCES));
      return { success: true };
    }

    setLoading(true);
    setError(null);

    try {
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/api/preferences/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreferences(response.data);
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(response.data));
      return { success: true };
    } catch (err) {
      console.error('Failed to reset preferences:', err);
      const message = err.response?.data?.detail || 'Failed to reset preferences';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getToken]);

  const value = {
    preferences,
    loading,
    error,
    updatePreferences,
    resetPreferences,
    refetch: fetchPreferences,
    DEFAULT_PREFERENCES,
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
}

export default PreferencesContext;
